import React, { useState, useRef } from "react";
import { ClipboardList, Target, Save, ClipboardPlus, FilePlus2, Loader2 } from "lucide-react";
import DashboardModal from "@/components/ui/DashboardModal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TaskForm, { TaskFormValues } from "@/components/tasks/form/TaskForm";
import { ProjectTitleField, ProjectDescriptionField, ProjectTargetDateField } from "@/components/goal-planner/form-fields";
import { Button } from "@/components/ui/button";
import { useAddGoalMutation } from "@/hooks/goal-planner";
import { GoalCreationInput } from "@/hooks/goal-planner/goal-types";
import { toast } from "sonner";
import { Task } from "@/types/tasks";
import { useCreateTaskWithAssignees } from "@/hooks/tasks/use-create-task-with-assignees";
import { supabase } from "@/integrations/supabase/client";

interface UnifiedCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTaskCreated?: (taskData: Omit<Task, 'id' | 'isCompleted' | 'createdAt'>) => void;
  onGoalCreated?: (goalId: string) => void;
  defaultTab?: 'task' | 'goal';
  hideTabs?: boolean; // New prop to hide the tab system
  goalId?: string; // Auto-set goal ID when used in project context
  // NEW: Context-aware props following New Task system pattern
  context?: 'task-manager' | 'project';
  showAssignees?: boolean;
}

const UnifiedCreationModal: React.FC<UnifiedCreationModalProps> = ({
  isOpen,
  onClose,
  onTaskCreated,
  onGoalCreated,
  defaultTab = 'task',
  hideTabs = false,
  goalId: initialGoalId,
  context,
  showAssignees
}) => {

  const [activeTab, setActiveTab] = useState<'task' | 'goal'>(defaultTab);
  
  // Goal form state
  const [goalTitle, setGoalTitle] = useState('');
  const [goalDescription, setGoalDescription] = useState('');
  const [goalTargetDate, setGoalTargetDate] = useState<Date | null>(null);
  
  // Hook for project task creation with assignees
  const projectTaskCreationMutation = useCreateTaskWithAssignees();
  
  // Loading state for standalone task creation
  const [isCreatingStandaloneTask, setIsCreatingStandaloneTask] = useState(false);
  
  // Context-based defaults following New Task system pattern
  const finalShowAssignees = showAssignees !== undefined 
    ? showAssignees 
    : context === 'project'; // Show assignees only in project context
  
  // Task form initial values
  const taskInitialValues: TaskFormValues = {
    title: '',
    description: '',
    dueDate: (() => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    })(),
    priority: null,
    goalId: initialGoalId || null,
    assigneeIds: [],
  };

  const { mutate: addGoal, isPending: isAddingGoal } = useAddGoalMutation();

  const handleCancel = () => {
    // Reset form state
    setGoalTitle('');
    setGoalDescription('');
    setGoalTargetDate(null);
    onClose();
  };

  const handleTaskSubmit = async (values: TaskFormValues) => {
    const { assigneeIds = [], goalId, title, description, dueDate, priority } = values;
    
    // Use auto-set goalId if provided, otherwise require manual selection
    const finalGoalId = goalId || initialGoalId;

    // Context-aware validation: Only require project when NOT in task-manager context
    if (!finalGoalId && !initialGoalId && context !== "task-manager") {
      toast.error('Please select a project');
      return;
    }

    try {
      if (finalGoalId) {
        // PROJECT TASK: Use create_task_with_assignees for tasks with a goalId
        const result = await projectTaskCreationMutation.mutateAsync({
          title,
          goalId: finalGoalId,
          description,
          dueDate,
          priority,
          assigneeIds
        });
        
        // Call the original callback if provided (for compatibility)
        if (onTaskCreated && result.success && result.task_id) {
          const finalTaskData = {
            title,
            description,
            dueDate,
            priority,
            goalId: finalGoalId
          };
          onTaskCreated(finalTaskData);
        }
      } else {
        // STANDALONE TASK: Use create_task_simple for tasks without a goalId
        setIsCreatingStandaloneTask(true);
        
        const { data, error } = await supabase.rpc('create_task_simple', {
          p_title: title,
          p_description: description || null,
          p_due_date: dueDate || null,
          p_priority: priority || null,
          p_urgency: null // Urgency not used in simple task creation
        });

        if (error) {
          throw new Error(error.message);
        }

        if (!data) {
          throw new Error('No response from server');
        }

        // Parse JSON response
        const result = typeof data === 'string' ? JSON.parse(data) : data;

        if (!result.success) {
          throw new Error(result.error || 'Task creation failed');
        }

        // Call the original callback if provided (for compatibility)
        if (onTaskCreated) {
          const finalTaskData = {
            title,
            description,
            dueDate,
            priority,
            goalId: null // Standalone task has no goalId
          };
          onTaskCreated(finalTaskData);
        }

        toast.success('Task created successfully!');
      }
      
      onClose();
    } catch (error) {
      console.error('Task creation failed:', error);
      toast.error(`Failed to create task: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsCreatingStandaloneTask(false);
    }
  };

  const handleGoalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!goalTitle.trim()) {
      toast.error('Please enter a goal title');
      return;
    }

    const goalData: GoalCreationInput = {
      title: goalTitle.trim(),
      description: goalDescription.trim() || '',
      dueDate: goalTargetDate ? goalTargetDate.toISOString().split('T')[0] : undefined
    };

    try {
      await new Promise((resolve, reject) => {
        addGoal(goalData, {
          onSuccess: (data) => {
            if (onGoalCreated) {
              onGoalCreated(data.id);
            }
            onClose();
            resolve(data);
          },
          onError: (error) => {
            reject(error);
          }
        });
      });
    } catch (error) {
      console.error('Goal creation failed:', error);
    }
  };

  // Reset tab to default when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab);
    }
  }, [isOpen, defaultTab]);

  // Context-aware title generation
  const getModalTitle = () => {
    if (hideTabs) {
      // When hideTabs is true, we're creating a task
      return context === 'project' ? 'Create Team Task' : 'Create Your Task';
    }
    // When tabs are visible, show different titles based on active tab
    if (activeTab === 'task') {
      return context === 'project' ? 'Create Team Task' : 'Create Your Task';
    }
    return 'Create Goal';
  };

  return (
    <DashboardModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={getModalTitle()}
      size="md"
      className="max-w-2xl"
      accentColor="purple"
    >
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'task' | 'goal')} className="w-full">
        {!hideTabs && (
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="goal" className="flex items-center gap-2">
              <Target size={16} />
              Goal
            </TabsTrigger>
            <TabsTrigger value="task" className="flex items-center gap-2">
              <ClipboardPlus size={16} />
              Task
            </TabsTrigger>
          </TabsList>
        )}

        <TabsContent value="task" className="mt-0">
          <TaskForm
            initialValues={taskInitialValues}
            onSubmit={handleTaskSubmit}
            onCancel={handleCancel}
            submitButtonText={isCreatingStandaloneTask ? "Creating Task..." : (projectTaskCreationMutation.isPending ? "Creating Task..." : "Create Task")}
            hideGoalSelector={!!initialGoalId}
            showAssignees={finalShowAssignees}
          />
        </TabsContent>

        <TabsContent value="goal" className="mt-0">
          <form onSubmit={handleGoalSubmit} className="space-y-6">
            <ProjectTitleField
              title={goalTitle}
              setTitle={setGoalTitle}
            />
            
            <ProjectDescriptionField
              description={goalDescription}
              setDescription={setGoalDescription}
            />
            
            <ProjectTargetDateField
              targetDate={goalTargetDate}
              setTargetDate={setGoalTargetDate}
            />

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isAddingGoal || !goalTitle.trim()}
                className="flex-1 flex items-center gap-2"
              >
                {isAddingGoal ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <FilePlus2 size={16} />
                )}
                {isAddingGoal ? "Creating..." : "Create Goal"}
              </Button>
            </div>
          </form>
        </TabsContent>
      </Tabs>
    </DashboardModal>
  );
};

export default UnifiedCreationModal; 